import React, { useEffect, useMemo, useState } from "react";
import api, { downloadHandover, downloadAssetReport } from "../../api";
import { toast } from "sonner";
import { Laptop, Smartphone, User, Search, FileSignature, History, PackageCheck, UserCog, FileDown, ChevronDown, CheckCircle2, CircleSlash } from "lucide-react";
import { fmtDate, contractBadge, daysToEnd } from "./adminUtils";
import { ConfirmDialog } from "./ConfirmDialog";
import { AssetHistoryDialog } from "./AssetHistoryDialog";
import { AssetReassignDialog } from "./AssetReassignDialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const StatCard = ({ icon, label, value, color, testid, alert }) => (
  <div className="relative bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center gap-3" data-testid={testid}>
    {alert?.count > 0 && (
      <span data-testid={`${testid}-alert`} className={`absolute -top-2 -right-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-sm ${alert.danger ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`} title={`${alert.count} in scadenza`}>
        {alert.count} in scadenza
      </span>
    )}
    <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}1a`, color }}>{icon}</span>
    <div>
      <div className="text-[22px] font-black leading-none text-[#16345c]">{value}</div>
      <div className="text-[11.5px] text-gray-500 mt-1">{label}</div>
    </div>
  </div>
);

const ExpiryCell = ({ iso }) => {
  if (!iso) return <span className="text-gray-400 text-[13px]">—</span>;
  const badge = contractBadge(iso);
  return (
    <div className="flex flex-col">
      <span className="text-[13px] text-gray-700">{fmtDate(iso)}</span>
      {badge && <span className={`w-fit mt-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>}
    </div>
  );
};

export const DevicesTab = () => {
  const [benefits, setBenefits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [showReturned, setShowReturned] = useState(false);
  const [toReturn, setToReturn] = useState(null);
  const [histView, setHistView] = useState(null);
  const [toReassign, setToReassign] = useState(null);
  const [savingReassign, setSavingReassign] = useState(false);

  const load = () => api.get("/benefits").then((r) => setBenefits(r.data || [])).catch(() => {});
  useEffect(() => { load(); api.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => {}); }, []);

  const handover = async (bid, asset) => {
    try { await downloadHandover(bid, asset, `verbale_consegna_${asset}.pdf`); toast.success("Verbale di consegna scaricato"); }
    catch { toast.error("Errore durante il download"); }
  };

  const report = async (includeReturned) => {
    try { await downloadAssetReport("device", "report_parco_dotazioni.pdf", includeReturned); toast.success("Report parco dotazioni scaricato"); }
    catch { toast.error("Errore durante il download"); }
  };

  const stats = useMemo(() => {
    let pcAssigned = 0, pcFree = 0, phAssigned = 0, phFree = 0;
    let pcExp = 0, pcDanger = false, phExp = 0, phDanger = false;
    (benefits || []).forEach((b) => {
      if ((b.pc_model || b.pc_serial) && !b.pc_returned) {
        b.pc_employee_id ? pcAssigned++ : pcFree++;
        const d = daysToEnd(b.pc_return_date);
        if (d !== null && d <= 30) { pcExp++; if (d < 0) pcDanger = true; }
      }
      if ((b.phone_model || b.phone_imei || b.phone_line) && !b.phone_returned) {
        b.phone_employee_id ? phAssigned++ : phFree++;
        const d = daysToEnd(b.phone_return_date);
        if (d !== null && d <= 30) { phExp++; if (d < 0) phDanger = true; }
      }
    });
    return { pcAssigned, pcFree, phAssigned, phFree, pcExp, pcDanger, phExp, phDanger };
  }, [benefits]);

  const markReturned = async () => {
    const d = toReturn;
    try {
      await api.put(`/benefits/${d.bid}`, { [`${d.asset}_returned`]: true, [`${d.asset}_returned_date`]: new Date().toISOString().slice(0, 10) });
      toast.success("Bene segnato come riconsegnato");
      setToReturn(null); load();
    } catch { toast.error("Errore"); }
  };

  const doReassign = async ({ id, name }) => {
    const d = toReassign;
    setSavingReassign(true);
    try {
      await api.put(`/benefits/${d.bid}`, { [`${d.asset}_employee_id`]: id, [`${d.asset}_employee_name`]: name, [`${d.asset}_returned`]: false, [`${d.asset}_returned_date`]: "" });
      toast.success(id ? `Bene riassegnato a ${name}` : "Bene lasciato senza assegnatario");
      setToReassign(null); load();
    } catch { toast.error("Errore durante la riassegnazione"); }
    finally { setSavingReassign(false); }
  };

  const devices = useMemo(() => {
    const list = [];
    (benefits || []).forEach((b) => {
      if (b.pc_model || b.pc_serial) {
        list.push({ id: `${b.id}-pc`, bid: b.id, asset: "pc", tipo: "PC", model: b.pc_model, ident: b.pc_serial, assignee: b.pc_employee_name, empId: b.pc_employee_id, ret: b.pc_return_date, returned: b.pc_returned, returnedDate: b.pc_returned_date, history: b.pc_history });
      }
      if (b.phone_model || b.phone_imei || b.phone_line) {
        const ident = [b.phone_line ? `Linea ${b.phone_line}` : "", b.phone_imei ? `IMEI ${b.phone_imei}` : ""].filter(Boolean).join(" · ");
        list.push({ id: `${b.id}-phone`, bid: b.id, asset: "phone", tipo: "Telefono", model: b.phone_model, ident, assignee: b.phone_employee_name, empId: b.phone_employee_id, ret: b.phone_return_date, returned: b.phone_returned, returnedDate: b.phone_returned_date, history: b.phone_history });
      }
    });
    const q = search.trim().toLowerCase();
    return list.filter((d) => (showReturned || !d.returned) && (!q || `${d.tipo} ${d.model || ""} ${d.ident || ""} ${d.assignee || ""}`.toLowerCase().includes(q)));
  }, [benefits, search, showReturned]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 text-[13px] text-gray-600">
        <Laptop size={18} className="text-emerald-600" />
        Elenco delle dotazioni informatiche e telefoniche assegnate. Gestiscile dalla pagina <b>Welfare</b>.
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="devices-stats">
        <StatCard testid="stat-pc-assigned" icon={<Laptop size={18} />} value={stats.pcAssigned} label="PC assegnati" color="#16a34a" alert={{ count: stats.pcExp, danger: stats.pcDanger }} />
        <StatCard testid="stat-pc-free" icon={<CircleSlash size={18} />} value={stats.pcFree} label="PC disponibili" color="#e67e22" />
        <StatCard testid="stat-phone-assigned" icon={<Smartphone size={18} />} value={stats.phAssigned} label="Telefoni assegnati" color="#6366f1" alert={{ count: stats.phExp, danger: stats.phDanger }} />
        <StatCard testid="stat-phone-free" icon={<Smartphone size={18} />} value={stats.phFree} label="Telefoni liberi" color="#e67e22" />
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center border border-gray-300 rounded-sm px-3 bg-white w-full sm:w-[320px]">
          <Search size={16} className="text-gray-400" />
          <input value={search} data-testid="device-search" onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per modello, seriale, linea o dipendente..." className="flex-1 py-2 px-2 text-[14px] outline-none" />
        </div>
        <label className="flex items-center gap-2 text-[13px] text-gray-600 cursor-pointer">
          <input type="checkbox" checked={showReturned} data-testid="device-show-returned" onChange={(e) => setShowReturned(e.target.checked)} className="accent-[#16345c]" />
          Mostra riconsegnati
        </label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="device-report-btn" className="ml-auto flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2 rounded-sm transition-colors"><FileDown size={15} /> Report parco dotazioni <ChevronDown size={14} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[250px]">
            <DropdownMenuItem data-testid="device-report-all" onClick={() => report(true)} className="text-[13px] cursor-pointer gap-2"><CheckCircle2 size={15} className="text-[#16345c]" /> Tutti i beni (con riconsegnati)</DropdownMenuItem>
            <DropdownMenuItem data-testid="device-report-active" onClick={() => report(false)} className="text-[13px] cursor-pointer gap-2"><Laptop size={15} className="text-emerald-600" /> Solo dotazioni attive</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-testid="devices-registry">
        <table className="w-full min-w-[880px]">
          <thead>
            <tr className="bg-[#16345c] text-white text-[12px] uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-bold">Tipo</th>
              <th className="text-left px-4 py-3 font-bold">Modello</th>
              <th className="text-left px-4 py-3 font-bold">Identificativo</th>
              <th className="text-left px-4 py-3 font-bold">Assegnato a</th>
              <th className="text-left px-4 py-3 font-bold">Riconsegna</th>
              <th className="text-right px-4 py-3 font-bold">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => (
              <tr key={d.id} className={`border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors ${d.returned ? "opacity-60" : ""}`} data-testid={`device-row-${d.id}`}>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1.5 text-[12px] font-bold px-2 py-1 rounded-full ${d.tipo === "PC" ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-700"}`}>
                    {d.tipo === "PC" ? <Laptop size={13} /> : <Smartphone size={13} />} {d.tipo}
                  </span>
                </td>
                <td className="px-4 py-3 text-[14px] font-bold text-[#16345c]">
                  {d.model || <span className="text-gray-400 font-normal">—</span>}
                  {d.returned && <span className="ml-2 text-[10px] font-bold uppercase bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">Riconsegnato {fmtDate(d.returnedDate)}</span>}
                </td>
                <td className="px-4 py-3 text-[12.5px] text-gray-700 font-mono">{d.ident || <span className="text-gray-400 font-sans">—</span>}</td>
                <td className="px-4 py-3 text-[13px] text-gray-700">
                  {d.assignee ? <span className="flex items-center gap-1.5"><User size={13} className="text-gray-400" /> {d.assignee}</span> : <span className="text-gray-400">Non assegnato</span>}
                </td>
                <td className="px-4 py-3">{d.returned ? <span className="text-gray-400 text-[13px]">—</span> : <ExpiryCell iso={d.ret} />}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setHistView({ title: `${d.tipo} · ${d.model || ""}`, history: d.history })} data-testid={`device-history-${d.id}`} title="Storico assegnazioni" className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-gray-500 hover:bg-gray-100 transition-colors"><History size={15} /></button>
                    <button onClick={() => setToReassign({ bid: d.bid, asset: d.asset, label: `${d.tipo} · ${d.model || ""}`, currentEmployeeId: d.empId || "none" })} data-testid={`device-reassign-${d.id}`} title="Riassegna rapida" className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-[#0d6cb8] hover:bg-blue-50 transition-colors"><UserCog size={15} /></button>
                    <button onClick={() => handover(d.bid, d.asset)} data-testid={`device-handover-${d.id}`} title="Verbale di consegna (PDF)" className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-[#16345c] hover:bg-blue-50 transition-colors"><FileSignature size={15} /></button>
                    {!d.returned && <button onClick={() => setToReturn(d)} data-testid={`device-return-${d.id}`} title="Segna come riconsegnato" className="w-8 h-8 inline-flex items-center justify-center rounded-sm text-emerald-600 hover:bg-emerald-50 transition-colors"><PackageCheck size={15} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {devices.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400 text-[13px]">Nessuna dotazione {showReturned ? "" : "attiva "}trovata.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog open={!!toReturn} onCancel={() => setToReturn(null)} onConfirm={markReturned} confirmLabel="Segna riconsegnato" confirmClass="bg-emerald-600 hover:bg-emerald-700" title="Segnare il bene come riconsegnato?">
        Il bene <b>{toReturn?.model}</b> verrà segnato come riconsegnato in data odierna e uscirà dagli avvisi e dal registro attivo. Puoi rivederlo con "Mostra riconsegnati".
      </ConfirmDialog>

      <AssetHistoryDialog open={!!histView} onClose={() => setHistView(null)} title={histView?.title} history={histView?.history} />

      <AssetReassignDialog open={!!toReassign} onClose={() => setToReassign(null)} onConfirm={doReassign} employees={employees} target={toReassign} saving={savingReassign} />
    </div>
  );
};
